import React, { memo, useEffect, useRef } from 'react'
import { DatePicker } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import dayjs, { Dayjs } from 'dayjs'
import type { RangePickerProps } from 'antd/es/date-picker'
import { Period } from '../../../../../types/fiscalYear/FiscalYear'

const { RangePicker } = DatePicker

interface DateRangeValue {
  startPeriod: Period
  endPeriod: Period
}

// Helper: Convert Period to Dayjs
const periodToDayjs = (period: Period): Dayjs => {
  return dayjs(`${period.year}-${String(period.month).padStart(2, '0')}-01`)
}

// Helper: Convert Dayjs to Period
const dayjsToPeriod = (date: Dayjs): Period => {
  return {
    period: 0, // Not used
    year: date.year(),
    month: date.month() + 1
  }
}

/**
 * Date Range Cell Editor for history range selection
 *
 * Allows user to select a date range using Ant Design RangePicker
 */
const DateRangeCellEditor = memo(
  ({ value, onValueChange, stopEditing }: CustomCellEditorProps<any, DateRangeValue>) => {
    const pickerRef = useRef<any>(null)

    useEffect(() => {
      // Auto-open picker when editor starts
      setTimeout(() => {
        pickerRef.current?.focus()
      }, 0)
    }, [])

    const rangeValue: [Dayjs, Dayjs] | null = value
      ? [periodToDayjs(value.startPeriod), periodToDayjs(value.endPeriod)]
      : null

    const handleChange: RangePickerProps['onChange'] = dates => {
      if (dates && dates[0] && dates[1]) {
        const [start, end] = dates
        const newValue: DateRangeValue = {
          startPeriod: dayjsToPeriod(start),
          endPeriod: dayjsToPeriod(end)
        }
        onValueChange(newValue)
        // Close editor after selection
        setTimeout(() => stopEditing(), 100)
      } else {
        onValueChange(null)
      }
    }

    return (
      <RangePicker
        ref={pickerRef}
        value={rangeValue}
        onChange={handleChange}
        picker="month"
        format="MM/YYYY"
        placeholder={['Alku', 'Loppu']}
        open
        onOpenChange={open => {
          if (!open) {
            stopEditing()
          }
        }}
        style={{ width: '100%' }}
        getPopupContainer={trigger => trigger.parentElement || document.body}
      />
    )
  }
)

DateRangeCellEditor.displayName = 'DateRangeCellEditor'

export default DateRangeCellEditor
